import { useEffect } from 'react';
import { applyThemeToDom } from '../context/ThemeContext';

/**
 * Force the page to render in dark mode while the calling component is mounted,
 * then restore the authoritative (persisted) theme on unmount.
 *
 * Used by public-facing pages (marketing landing + auth) so they ALWAYS match the
 * premium dark TrainAxis brand — regardless of the visitor's saved localStorage
 * theme, system theme, or a logged-in user's dashboard theme.
 *
 * Why a MutationObserver?
 *   ThemeProvider applies the saved (possibly 'light') theme to <html>/<body> in
 *   its own effects, which can run AFTER this page's effect on a fresh page load
 *   and override the forced dark — causing the public page to flash/stick to light
 *   in browsers whose saved theme is 'light'. The observer re-asserts dark the
 *   instant anything sets a non-dark theme, so dark always wins while mounted.
 *
 * On unmount it restores the persisted dashboard theme (`trainaxis_theme`, which
 * ThemeProvider keeps in sync with the backend preference) — so returning to the
 * dashboard never strands it in the wrong theme. It never writes localStorage or
 * touches the saved preference itself.
 */
export default function useForceDarkTheme() {
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    applyThemeToDom('dark');

    const force = () => {
      const dark =
        root.dataset.theme === 'dark' && body.dataset.theme === 'dark' &&
        root.classList.contains('dark') && body.classList.contains('dark');
      if (!dark) applyThemeToDom('dark');
    };

    const observer = new MutationObserver(force);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    observer.observe(body, { attributes: true, attributeFilter: ['data-theme', 'class'] });

    return () => {
      observer.disconnect();
      // Restore the authoritative dashboard theme persisted by ThemeProvider.
      applyThemeToDom(localStorage.getItem('trainaxis_theme'));
    };
  }, []);
}
